import type { HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';

/**
 * Loading placeholders in the design language: flat sand blocks with the
 * spec's radii (§2.4), pulsing only when the user allows motion.
 *
 * - `Skeleton`: one block. Size it with Tailwind (`h-5 w-40`).
 * - `SkeletonText`: N text lines, the last one shorter.
 * - `SkeletonGroup`: the accessible wrapper. One `role="status"` region
 *   with a translated "Loading…" label, so screen readers hear one
 *   announcement and not one per block (the blocks are `aria-hidden`).
 */
export type SkeletonRadius = 'pill' | 10 | 12 | 14 | 16 | 18 | 20 | 24;

const radii: Record<SkeletonRadius, string> = {
  pill: 'rounded-full',
  10: 'rounded-10',
  12: 'rounded-12',
  14: 'rounded-14',
  16: 'rounded-16',
  18: 'rounded-18',
  20: 'rounded-20',
  24: 'rounded-24',
};

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  radius?: SkeletonRadius;
  /** `sand` on bg (default), `sand2` on sand surfaces. */
  tone?: 'sand' | 'sand2';
}

export function Skeleton({
  radius = 12,
  tone = 'sand',
  className,
  ...rest
}: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      data-skeleton=""
      className={cn(
        'block motion-safe:animate-pulse',
        tone === 'sand' ? 'bg-sl-sand' : 'bg-sl-sand-2',
        radii[radius],
        className
      )}
      {...rest}
    />
  );
}

export interface SkeletonTextProps {
  lines?: number;
  className?: string;
  /** Classes for each line (height). Default 14px. */
  lineClassName?: string;
}

export function SkeletonText({
  lines = 3,
  className,
  lineClassName,
}: SkeletonTextProps) {
  return (
    <div aria-hidden="true" className={cn('flex flex-col gap-2.5', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          radius="pill"
          className={cn(
            'h-3.5',
            i === lines - 1 && lines > 1 ? 'w-3/5' : 'w-full',
            lineClassName
          )}
        />
      ))}
    </div>
  );
}

export interface SkeletonGroupProps {
  /** Announced to screen readers. Defaults to the translated "Loading…". */
  label?: string;
  className?: string;
  children: ReactNode;
}

export function SkeletonGroup({ label, className, children }: SkeletonGroupProps) {
  const { t } = useTranslation();
  return (
    <div role="status" aria-live="polite" className={className}>
      <span className="sr-only">{label ?? t('common.loading')}</span>
      {children}
    </div>
  );
}
